import React from 'react';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { usePagination } from 'further-ui/hooks';
import FormWrapper, {
  AddCommitmentsToEqualisationFormValues,
} from './FormWrapper';
import AddCommitmentsToEqualisationForm from './AddCommitmentsToEqualisationForm';
import {
  calculateCPCapitalFromPercentage,
  getCommitmentsWithProRataCapitalContribution,
  getCommitmentsWithFixedCapitalContribution,
  getCommitmentsForManualCapitalContribution,
} from './utils';
import { sumBy } from 'lodash';
import { CapitalContributionRule } from 'further-types/lp/vehicle';
import { CalendarDay } from 'further-ui/utils';

const pageId = 'commitments-for-equalisations';

interface AddCommitmentsToEqualisationProps {
  savedEqualisation?: {
    selectedCommitments: Array<{
      commitmentId: string;
      investorName: string;
      isCarryPartnerCommitment: boolean;
      totalCommitmentLcy: number;
      loanCommitmentLcy: number;
      capitalCommitmentLcy: number;
      isCapitalContributionRuleOverride?: boolean;
    }>;
    name: string;
    closeDate: CalendarDay;
    id: string;
  };
}

const AddCommitmentsToEqualisation: React.FC<
  AddCommitmentsToEqualisationProps
> = ({ savedEqualisation }: AddCommitmentsToEqualisationProps) => {
  const {
    id: savedEqualisationId,
    name: savedEqualisationName,
    closeDate: savedCloseDate,
    selectedCommitments: savedSelectedCommitments,
  } = savedEqualisation ?? {};

  const pagination = usePagination({
    orderBy: 'commitmentDate',
    order: 'desc',
    id: pageId,
  });

  const { vehicle } = useLayoutContext();
  const [commitmentsData] = api.commitments.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
    ...pagination.properties,
  });

  const isProRata =
    vehicle?.capitalContributionRule ===
    CapitalContributionRule.ProRataOfFixedPartnershipCapital;
  const isFixedCapitalContribution =
    vehicle?.capitalContributionRule ===
    CapitalContributionRule.FixedCommitmentPercentage;

  const commitmentsWithSelected = (
    commitmentsData?.results?.map((c) => {
      const savedCommitment = savedSelectedCommitments?.find(
        (savedCommitment) => savedCommitment.commitmentId === c.id,
      );
      return {
        ...c,
        loanCommitmentLcy:
          savedCommitment?.loanCommitmentLcy ?? c.loanCommitmentLcy,
        capitalCommitmentLcy:
          isProRata &&
          !savedCommitment?.isCapitalContributionRuleOverride &&
          !c.isCarryPartnerCommitment &&
          !c.isCapitalContributionRuleOverride
            ? 0
            : savedCommitment?.capitalCommitmentLcy ?? c.capitalCommitmentLcy,
        totalCommitmentLcy:
          savedCommitment?.totalCommitmentLcy ?? c.totalCommitmentLcy,
        isCapitalContributionRuleOverride:
          savedCommitment?.isCapitalContributionRuleOverride ??
          c.isCapitalContributionRuleOverride,
        isSelected: !savedSelectedCommitments?.length
          ? true
          : !!savedCommitment || !!c?.equalisationDate,
        equalisationDate: c.equalisationDate,
        existingCapitalCommitmentLcy: c.capitalCommitmentLcy,
        existingLoanCommitmentLcy: c.loanCommitmentLcy,
        existingTotalCommitmentLcy: c.totalCommitmentLcy,
      };
    }) ?? []
  ).sort((a) => (a.equalisationDate ? -1 : 1));

  const commitments = isProRata
    ? getCommitmentsWithProRataCapitalContribution({
        commitments: commitmentsWithSelected,
        vehicleFixedCapitalAmount: vehicle?.fixedCapitalAmount ?? 0,
        capitalContributionPercentage:
          vehicle?.carrySettings?.capitalContributionPercentage,
      })
    : isFixedCapitalContribution
    ? getCommitmentsWithFixedCapitalContribution({
        commitments: commitmentsWithSelected,
        capitalContributionPercentage: vehicle?.capitalContributionPercentage,
      })
    : getCommitmentsForManualCapitalContribution({
        commitments: commitmentsWithSelected,
      });

  const nonCarryPartnerCapitalContributionTotalLcy = sumBy(
    commitments.filter((c) => !c.isCarryPartnerCommitment),
    'capitalCommitmentLcy',
  );

  const mappedCommitments = commitments.map((commitment) => {
    const isCpUsesCCPercentageAndIsNotProRata =
      commitment.isCarryPartnerCommitment &&
      !commitment.isCapitalContributionRuleOverride &&
      vehicle?.carrySettings?.capitalContributionPercentage &&
      vehicle?.capitalContributionRule !==
        CapitalContributionRule.ProRataOfFixedPartnershipCapital;

    const capitalCommitmentLcy = isCpUsesCCPercentageAndIsNotProRata
      ? calculateCPCapitalFromPercentage({
          capitalContributionPercentage:
            vehicle?.carrySettings?.capitalContributionPercentage ?? 0,
          nonCarryPartnerCapitalContributionTotalLcy,
        })
      : commitment.capitalCommitmentLcy;

    const totalCommitmentLcy = isCpUsesCCPercentageAndIsNotProRata
      ? capitalCommitmentLcy + (commitment.loanCommitmentLcy ?? 0)
      : commitment.totalCommitmentLcy;

    return {
      id: commitment.id,
      investorName: commitment.investorName,
      totalCommitmentLcy,
      loanCommitmentLcy: commitment.loanCommitmentLcy,
      capitalCommitmentLcy,
      isCarryPartnerCommitment: commitment.isCarryPartnerCommitment ?? false,
      isCapitalContributionRuleOverride:
        commitment.isCapitalContributionRuleOverride ?? false,
      isSelected: commitment.isSelected,
      equalisationDate: commitment.equalisationDate,
      existingCapitalCommitmentLcy: commitment.existingCapitalCommitmentLcy,
      existingLoanCommitmentLcy: commitment.existingLoanCommitmentLcy,
      existingTotalCommitmentLcy: commitment.existingTotalCommitmentLcy,
    };
  });

  const formValues: AddCommitmentsToEqualisationFormValues = {
    closeDate: savedCloseDate ?? new CalendarDay(new Date()),
    name: savedEqualisationName ?? '',
    commitments: mappedCommitments,
  };

  return (
    <FormWrapper values={formValues}>
      <AddCommitmentsToEqualisationForm
        savedEqualisationId={savedEqualisationId}
      />
    </FormWrapper>
  );
};

export default AddCommitmentsToEqualisation;
